import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';
import { formatInstant } from '@/lib/utils/date';
import { gaDays } from './calculations';
import { activeEvents } from './projection';
import type { ClinicalEvent, ClinicalSubject } from './types';
export interface FetalHeartPoint {
  date: string;
  ga?: number;
  eventId: string;
  encounterId: string;
  bpm?: number;
  low_bpm?: number;
  high_bpm?: number;
}
const positive = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;
/** Keep each observation and each fetus separate; do not manufacture a midpoint for a range. */
export function fetalHeartSeries(
  events: ClinicalEvent[],
  subjects: ClinicalSubject[],
  timeZone: string,
  edd?: string | null,
) {
  const active = activeEvents(events);
  return subjects
    .filter((subject) => subject.subject_type === 'FETUS')
    .map((subject) => ({
      subject,
      points: active
        .filter((event) => event.subject_id === subject.id && event.event_type === 'PRENATAL.FHT')
        .sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at) || a.sequence - b.sequence)
        .flatMap((event) => {
          const p = event.payload;
          const point: FetalHeartPoint = {
            date: formatInstant(event.occurred_at, 'yyyy-MM-dd HH:mm', timeZone),
            eventId: event.id,
            encounterId: event.encounter_id,
          };
          const days = gaDays(formatInstant(event.occurred_at, 'yyyy-MM-dd', timeZone), edd);
          if (days !== null) point.ga = days / 7;
          if (positive(p.bpm)) point.bpm = p.bpm;
          if (positive(p.low_bpm) && positive(p.high_bpm) && p.low_bpm <= p.high_bpm) {
            point.low_bpm = p.low_bpm;
            point.high_bpm = p.high_bpm;
          }
          return point.bpm !== undefined || point.low_bpm !== undefined ? [point] : [];
        }),
    }));
}
export function focusPrenatalVisit(encounterId: string) {
  const row = document.getElementById(`prenatal-visit-${encounterId}`);
  row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  row?.focus({ preventScroll: true });
}
export function FetalHeartTrends({
  events,
  subjects,
  timeZone,
  edd,
  onVisit = focusPrenatalVisit,
}: {
  events: ClinicalEvent[];
  subjects: ClinicalSubject[];
  timeZone: string;
  edd?: string | null;
  onVisit?: (encounterId: string) => void;
}) {
  const [show, setShow] = useState(true);
  const series = fetalHeartSeries(events, subjects, timeZone, edd);
  return (
    <section aria-label="Fetal heart tone trends" className="flex flex-col gap-4">
      <h3 className="font-medium">Fetal heart tones (bpm), per fetus</h3>
      <Button type="button" variant="outline" aria-expanded={show} onClick={() => setShow((v) => !v)}>
        {show ? 'Hide fetal heart tone charts' : 'Show fetal heart tone charts'}
      </Button>
      {show && (
        <>
          <p className="text-sm text-muted-foreground">
            Confirmed observations only. A documented range stays a range; no average is inferred. Select a plotted
            point or documented value to open its visit.
          </p>
          {!series.length && <p>No fetal subjects are recorded for this episode.</p>}
          <div className="grid gap-6 lg:grid-cols-2">
            {series.map(({ subject, points }) => (
              <section key={subject.id} aria-label={`Fetal heart tones: ${subject.label}`} className="min-w-0">
                <h4>{subject.label}</h4>
                {!points.length ? (
                  <p className="text-muted-foreground">No confirmed fetal heart tone observations.</p>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart
                        data={points}
                        onClick={(state) => {
                          const point = state?.activePayload?.[0]?.payload as FetalHeartPoint | undefined;
                          if (point) onVisit(point.encounterId);
                        }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey={edd ? 'ga' : 'date'}
                          type={edd ? 'number' : 'category'}
                          domain={['auto', 'auto']}
                        />
                        <YAxis domain={['auto', 'auto']} />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="linear"
                          dataKey="bpm"
                          name="Documented FHT"
                          stroke="var(--chart-1)"
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                        <Line
                          type="linear"
                          dataKey="low_bpm"
                          name="Range low"
                          stroke="var(--chart-2)"
                          strokeDasharray="4 2"
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                        <Line
                          type="linear"
                          dataKey="high_bpm"
                          name="Range high"
                          stroke="var(--chart-3)"
                          strokeDasharray="4 2"
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                    <details>
                      <summary>View documented values ({points.length})</summary>
                      <ul>
                        {points.map((point) => (
                          <li key={point.eventId}>
                            <Button
                              variant="link"
                              className="min-h-11 h-auto whitespace-normal text-left"
                              onClick={() => onVisit(point.encounterId)}>
                              {point.date} · {point.bpm !== undefined ? `${point.bpm} bpm` : ''}
                              {point.bpm !== undefined && point.low_bpm !== undefined ? ' · ' : ''}
                              {point.low_bpm !== undefined ? `${point.low_bpm}–${point.high_bpm} bpm` : ''} · Open visit
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </>
                )}
              </section>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
